<template>
  <b-overlay
    :show="show"
    spinner-variant="primary"
    spinner-type="grow"
    spinner-small
    rounded="sm"
  >
    <b-card>
      <div class="d-flex align-items-center mb-1">
        <b-button
          variant="flat-secondary"
          size="sm"
          :to="{ name: 'pamm_management_account' }"
        >
          <feather-icon
            :icon="$i18n.isRTL ? 'ArrowRightIcon' : 'ArrowLeftIcon'"
            size="14"
          />
          <span class="mx-25">{{ $t('back') }}</span>
        </b-button>
        <h4 class="card-title mb-0 mx-1">
          {{ $t('pamm_capital') }}
          <span
            v-if="account.nick_name"
            class="text-muted"
          >{{ account.nick_name }}</span>
        </h4>
      </div>
      <p class="text-muted mb-0">
        {{ $t('pamm_capital_description') }}
      </p>
    </b-card>

    <template v-if="entries.length > 0">
      <b-card>
        <b-row>
          <b-col
            v-for="tile in tiles"
            :key="tile.key"
            cols="6"
            lg
            class="mb-1 mb-lg-0"
          >
            <div class="pamm-capital__tile h-100">
              <small class="text-muted d-block">{{ $t(tile.label) }}</small>
              <h3
                class="font-weight-bolder mb-0 mt-25"
                :class="tile.colored ? valueClass(tile.value) : ''"
              >
                <span dir="ltr">{{ formatAmount(tile.value, tile.colored) }}</span>
              </h3>
            </div>
          </b-col>
        </b-row>
      </b-card>

      <b-card
        v-if="entries.length > 1"
        :title="$t('pamm_capital_history')"
      >
        <vue-apex-charts
          type="area"
          height="260"
          :options="chartOptions"
          :series="chartSeries"
        />
      </b-card>

      <b-card :title="$t('pamm_capital_entries')">
        <b-table
          :items="newestFirst"
          :fields="fields"
          :per-page="perPage"
          :current-page="currentPage"
          responsive
          stacked="md"
          hover
          class="mb-0"
        >
          <template #cell(effective_at)="data">
            <span class="text-nowrap">{{ formatDateTime(data.item.effective_at) }}</span>
          </template>
          <template #cell(entry_type)="data">
            <b-badge :variant="typeVariants[data.item.entry_type] || 'light-secondary'">
              {{ $te(`pamm_capital_entry_${data.item.entry_type}`) ? $t(`pamm_capital_entry_${data.item.entry_type}`) : data.item.entry_type }}
            </b-badge>
          </template>
          <template #cell(amount)="data">
            <span
              class="font-weight-bold text-nowrap"
              :class="valueClass(data.item.amount)"
              dir="ltr"
            >{{ formatAmount(data.item.amount, true) }}</span>
          </template>
          <template #cell(balance_after)="data">
            <span
              class="text-nowrap"
              dir="ltr"
            >{{ formatAmount(data.item.balance_after) }}</span>
          </template>
        </b-table>
        <div
          v-if="entries.length > perPage"
          class="d-flex justify-content-center mt-2"
        >
          <b-pagination
            v-model="currentPage"
            :total-rows="entries.length"
            :per-page="perPage"
            size="sm"
            class="mb-0"
          />
        </div>
      </b-card>
    </template>

    <b-card v-else-if="!show">
      <p class="text-center text-muted py-2 mb-0">
        {{ $t('pamm_capital_no_entries') }}
      </p>
    </b-card>
  </b-overlay>
</template>

<script>
import {
  BOverlay,
  BCard,
  BRow,
  BCol,
  BButton,
  BBadge,
  BTable,
  BPagination,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import jalaali from 'moment-jalaali'
import { $themeColors } from '@themeConfig'
import PammApis from '@/modules/pamm/service/apis'
import {
  field, listOf, toDate, toNumber,
} from '@/modules/pamm/utils/response'
import { numberWithCommas } from '@/utils/index'

const pammApis = new PammApis()

const getResults = res => {
  const { results } = res.data
  return Array.isArray(results) ? results : (results?.data ?? [])
}

/**
 * The manager's capital ledger (pamm_manager_capital_ledger): every change to the
 * manager's own capital. The capital is the running sum of the amounts.
 */
export default {
  components: {
    BOverlay,
    BCard,
    BRow,
    BCol,
    BButton,
    BBadge,
    BTable,
    BPagination,
    VueApexCharts,
  },
  data() {
    return {
      show: false,
      account: {},
      // oldest first, each with the capital after it
      entries: [],
      perPage: 10,
      currentPage: 1,
      typeVariants: {
        initial: 'light-primary',
        top_up: 'light-success',
        withdrawal: 'light-danger',
        settlement: 'light-info',
        correction: 'light-warning',
      },
    }
  },
  computed: {
    newestFirst() {
      return this.entries.slice().reverse()
    },
    tiles() {
      const sumOf = types => this.entries
        .filter(entry => types.includes(entry.entry_type))
        .reduce((total, entry) => total + entry.amount, 0)
      return [
        { key: 'capital', label: 'pamm_capital_current', value: this.entries[this.entries.length - 1].balance_after },
        { key: 'deposits', label: 'pamm_capital_deposits', value: sumOf(['initial', 'top_up']) },
        { key: 'withdrawals', label: 'pamm_capital_withdrawals', value: sumOf(['withdrawal']) },
        {
          key: 'results', label: 'pamm_capital_results', value: sumOf(['settlement']), colored: true,
        },
      ]
    },
    fields() {
      return [
        { key: 'effective_at', label: this.$t('date') },
        { key: 'entry_type', label: this.$t('type') },
        { key: 'amount', label: this.$t('amount') },
        { key: 'balance_after', label: this.$t('pamm_capital_balance_after') },
      ]
    },
    chartSeries() {
      return [{
        name: this.$t('pamm_capital'),
        data: this.entries.map(entry => Math.round(entry.balance_after * 100) / 100),
      }]
    },
    chartOptions() {
      return {
        chart: {
          toolbar: { show: false },
          zoom: { enabled: false },
          fontFamily: 'inherit',
        },
        colors: [$themeColors.primary],
        dataLabels: { enabled: false },
        stroke: { curve: 'stepline', width: 2 },
        fill: {
          type: 'gradient',
          gradient: { shadeIntensity: 0.9, opacityFrom: 0.4, opacityTo: 0.05 },
        },
        grid: { borderColor: 'rgba(34, 41, 47, 0.08)' },
        xaxis: {
          categories: this.entries.map(entry => this.formatDate(entry.effective_at)),
          labels: { hideOverlappingLabels: true },
          tooltip: { enabled: false },
        },
        yaxis: {
          labels: { formatter: value => numberWithCommas(Math.round(value * 100) / 100) },
        },
        tooltip: {
          y: { formatter: value => this.formatAmount(value) },
        },
      }
    },
  },
  mounted() {
    this.getData()
  },
  methods: {
    async getData() {
      this.show = true
      const { id } = this.$route.params
      await Promise.all([
        pammApis.getManagerCapital(id).then(res => {
          this.entries = this.readLedger(listOf(res))
        }).catch(err => {
          console.log(err)
        }),
        pammApis.getManagerList().then(res => {
          this.account = getResults(res).find(item => String(item.id) === String(id)) ?? {}
        }).catch(err => {
          console.log(err)
        }),
      ])
      this.show = false
    },
    // Oldest first, with the capital after each entry; worked out when the api does not send it.
    readLedger(rows) {
      let balance = 0
      return rows
        .map((row, index) => ({
          id: field(row, 'id') ?? index,
          effective_at: field(row, 'effective_at') ?? field(row, 'created_at'),
          entry_type: field(row, 'entry_type'),
          amount: toNumber(field(row, 'amount')) ?? 0,
          balance_after: toNumber(field(row, 'balance_after')),
        }))
        .sort((a, b) => ((toDate(a.effective_at)?.getTime() ?? 0) - (toDate(b.effective_at)?.getTime() ?? 0))
          || (Number(a.id) - Number(b.id)))
        .map(entry => {
          balance = Math.round((balance + entry.amount) * 100) / 100
          return { ...entry, balance_after: entry.balance_after ?? balance }
        })
    },
    valueClass(value) {
      const number = Number(value)
      if (!number) return ''
      return number > 0 ? 'text-success' : 'text-danger'
    },
    formatAmount(value, signed = false) {
      if (value === null || value === undefined || Number.isNaN(Number(value))) return '-'
      // + 0 turns -0 into 0
      const number = Math.round(Number(value) * 100) / 100 + 0
      return `${signed && number > 0 ? '+' : ''}${numberWithCommas(number.toFixed(2))} $`
    },
    formatDate(value) {
      const date = toDate(value)
      if (!date) return '-'
      return jalaali(date).format(this.$i18n.locale === 'fa' ? 'jYYYY/jMM/jDD' : 'YYYY-MM-DD')
    },
    formatDateTime(value) {
      const date = toDate(value)
      if (!date) return '-'
      return jalaali(date).format(this.$i18n.locale === 'fa' ? 'jYYYY/jMM/jDD HH:mm' : 'YYYY-MM-DD HH:mm')
    },
  },
}
</script>

<style lang="scss" scoped>
.pamm-capital__tile {
  padding: 0.75rem 1rem;
  border-radius: 0.428rem;
  background-color: rgba(34, 41, 47, 0.03);
}

.dark-layout .pamm-capital__tile {
  background-color: rgba(255, 255, 255, 0.04);
}
</style>
